import React from 'react';
import { Button, CircularProgress, FormControl } from '@mui/material';
import { useTranslation } from 'react-i18next';
import T, { bool, func } from 'prop-types';
import { FormElements } from 'web-components';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import {
  getDefaultFieldValue,
  getFormikError,
  getFormikHelperText,
  isLoading,
  validateBlankSpace
} from 'helpers/utils';
import { StyledContent, StyledContentGrid, StyledGridItem, StyledHandlers, StyledNewDialog } from '../../elements';
import { IrisBatchDetailsSchema } from '../../../../../attrs/formValidation';
import { IrisBatchDetailsPropTypes } from '../../../../../helpers/propTypes';
import { getSelectedIrisBatchDetails } from '../../../../../redux/machines_v2/selectors';
import { updateIrisBatchDetails } from '../../../../../redux/machines_v2/actions';
import { PlcType } from '../../../../../attrs/plcConfigValues';

const labelAmend = 'machines.machine_details.batch_iris.details';

const IrisBatchRecordsEditDetails = ({ open, machineId, handleClose, data, plcType }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { loadingState } = useSelector(getSelectedIrisBatchDetails);

  function isSaving() {
    return isLoading(loadingState.status);
  }

  const submitForm = (values, formikHelpers) => {
    dispatch(updateIrisBatchDetails(machineId, values));
    handleClose(true);
    formikHelpers.resetForm();
  };

  const actionBar = (handleSubmit, errors, resetForm, dirty) => (
    <StyledHandlers>
      <Button
        color="secondary"
        onClick={() => {
          resetForm();
          handleClose();
        }}
      >
        {t('customers.form.cancel')}
      </Button>
      <Button
        variant="contained"
        data-testid="plc-modal-submit"
        style={{ padding: '1rem 2.5rem' }}
        disabled={!dirty || Object.keys(errors).length !== 0 || isSaving()}
        onClick={() => handleSubmit()}
        onMouseDown={event => {
          event.preventDefault();
        }}
      >
        {t(`default_actions.save`)}
        {isSaving() && <CircularProgress sx={{ marginLeft: 1, fontSize: '1.125rem' }} size={18} />}
      </Button>
    </StyledHandlers>
  );

  const handleChangeValue = handleChange => event => {
    const { name, value } = event.target;

    let newValue = validateBlankSpace(value);

    if (plcType.plc_type === PlcType.IRIS_V3_INSIDE_S7) {
      newValue = newValue.toUpperCase();
    }

    handleChange({ target: { name, value: newValue } });
  };

  return (
    <Formik
      validationSchema={IrisBatchDetailsSchema(plcType)}
      initialValues={{
        batch_id_variable: getDefaultFieldValue(data.batch_id_variable),
        batch_size_variable: getDefaultFieldValue(data.batch_size_variable),
        end_time_variable: getDefaultFieldValue(data.end_time_variable),
        recipe_variable: getDefaultFieldValue(data.recipe_variable),
        start_time_variable: getDefaultFieldValue(data.start_time_variable)
      }}
      onSubmit={(values, formikHelpers) => submitForm(values, formikHelpers)}
      enableReinitialize
    >
      {({ errors, handleBlur, handleChange, touched, values, handleSubmit, resetForm, dirty }) => {
        const getHelperText = getFormikHelperText({ errors, t });
        const getError = getFormikError({ errors, touched });

        return (
          <StyledNewDialog
            open={open}
            handleClose={(event, reason) => {
              if (reason && reason === 'backdropClick') return;
              resetForm();
              handleClose();
            }}
            title={t(`${labelAmend}.modal_title`)}
            subTitle={t(`${labelAmend}.modal_subtitle`)}
            maxWidth="696px"
            displayActionBar="true"
            actionBar={actionBar(handleSubmit, errors, resetForm, dirty)}
            content={
              <StyledContent>
                <FormControl sx={{ width: '100%' }}>
                  <StyledContentGrid>
                    <StyledGridItem>
                      <FormElements.TextField
                        error={getError('batch_id_variable')}
                        helperText={getHelperText('batch_id_variable')}
                        fullWidth
                        id="batch_id_variable"
                        label={t(`${labelAmend}.batch_id_variable`)}
                        margin="none"
                        size="small"
                        name="batch_id_variable"
                        onBlur={handleBlur}
                        onChange={handleChangeValue(handleChange)}
                        value={getDefaultFieldValue(values.batch_id_variable)}
                        required
                      />
                    </StyledGridItem>
                    <StyledGridItem>
                      <FormElements.TextField
                        error={getError('recipe_variable')}
                        helperText={getHelperText('recipe_variable')}
                        fullWidth
                        id="recipe_variable"
                        label={t(`${labelAmend}.recipe_variable`)}
                        margin="none"
                        size="small"
                        name="recipe_variable"
                        onBlur={handleBlur}
                        onChange={handleChangeValue(handleChange)}
                        value={getDefaultFieldValue(values.recipe_variable)}
                      />
                    </StyledGridItem>
                    <StyledGridItem gridColumn="span 2">
                      <FormElements.TextField
                        error={getError('batch_size_variable')}
                        helperText={getHelperText('batch_size_variable')}
                        fullWidth
                        id="batch_size_variable"
                        label={t(`${labelAmend}.batch_size_variable`)}
                        margin="none"
                        size="small"
                        name="batch_size_variable"
                        onBlur={handleBlur}
                        onChange={handleChangeValue(handleChange)}
                        value={getDefaultFieldValue(values.batch_size_variable)}
                      />
                    </StyledGridItem>
                    <StyledGridItem>
                      <FormElements.TextField
                        error={getError('start_time_variable')}
                        helperText={getHelperText('start_time_variable')}
                        fullWidth
                        id="start_time_variable"
                        label={t(`${labelAmend}.start_time_variable`)}
                        margin="none"
                        size="small"
                        name="start_time_variable"
                        onBlur={handleBlur}
                        onChange={handleChangeValue(handleChange)}
                        value={getDefaultFieldValue(values.start_time_variable)}
                        required={plcType.plc_type === PlcType.IRIS_V3}
                      />
                    </StyledGridItem>
                    <StyledGridItem>
                      <FormElements.TextField
                        error={getError('end_time_variable')}
                        helperText={getHelperText('end_time_variable')}
                        fullWidth
                        id="end_time_variable"
                        label={t(`${labelAmend}.end_time_variable`)}
                        margin="none"
                        size="small"
                        name="end_time_variable"
                        onBlur={handleBlur}
                        onChange={handleChangeValue(handleChange)}
                        value={getDefaultFieldValue(values.end_time_variable)}
                        required={plcType.plc_type === PlcType.IRIS_V3}
                      />
                    </StyledGridItem>
                  </StyledContentGrid>
                </FormControl>
              </StyledContent>
            }
          />
        );
      }}
    </Formik>
  );
};

IrisBatchRecordsEditDetails.propTypes = {
  machineId: T.string,
  open: bool.isRequired,
  handleClose: func.isRequired,
  data: IrisBatchDetailsPropTypes,
  plcType: T.shape({
    plc_type: T.string,
    autoconfig_enabled: T.bool
  }).isRequired
};

IrisBatchRecordsEditDetails.defaultProps = {
  machineId: null,
  data: null
};

export default IrisBatchRecordsEditDetails;
